import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { startMeetingNow, reasonOf, adhocNotReady, PLATFORMS } from '../../lib/meetings';
import { authFor } from '../../lib/sessionLink';
import { useFeature } from '../../lib/features';
import { contractorSignedIn } from '../../lib/contractorAuth';
import { Dialog } from '../todos/TodoPeople';
import { Icon } from '../todos/icons';
import { captureSupported, captureHint } from './CallCapture';

/*
 * THE TWO BUTTONS FOR A MEETING NOBODY PLANNED (Gordon, 26 October).
 *
 *   START MEETING NOW   one press: a meeting for this moment is made (title
 *                       "Meeting – <date> <time>", me the organiser, nobody
 *                       else on it yet, in the room) and the in-room recorder
 *                       opens on it. Title and people are filled in while it
 *                       records, or afterwards.
 *   RECORD A CALL ON    a short dialog first - which app, and the firm's
 *   ANOTHER APP         consent reminder, which the person confirms - then a
 *                       'call' meeting is made and the recorder opens in its
 *                       capture mode (CallCapture.js: microphone plus the
 *                       other app's sound).
 *
 * Both are staff's (a contractor convening the firm's meetings is not a
 * thing) and both need Meetings AND Recording switched on for this person
 * (078): the server's gate on POST /meetings/now says the same.
 */

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/** The firm's consent rule (Settings -> Rules), for the words of the reminder. */
export function useConsentRule() {
  const [rule, setRule] = useState('announce');
  useEffect(() => {
    let live = true;
    axios.get(`${API_BASE}/app-settings`, authFor())
      .then((r) => { if (live && (r.data?.recording_consent === 'ask' || r.data?.recording_consent === 'announce')) setRule(r.data.recording_consent); })
      .catch(() => {});
    return () => { live = false; };
  }, []);
  return rule;
}

/** The firm's rule in the words of the moment. */
export const consentWords = (rule, where = 'room') => (rule === 'ask'
  ? `The firm’s rule is to ASK: ask everyone ${where === 'call' ? 'on the call' : 'in the room'} whether they agree to being recorded before you start.`
  : `The firm’s rule is to ANNOUNCE: tell everyone ${where === 'call' ? 'on the call' : 'in the room'} that it is being recorded before you start.`);

/** Whether this person may start one at all. */
export const useMayStart = () => {
  const meetingsOn = useFeature('meetings');
  const recordingOn = useFeature('recording');
  return meetingsOn && recordingOn && !contractorSignedIn();
};

export function StartNowButton({ className = '', onError, compact = false }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const may = useMayStart();
  if (!may) return null;
  const go = async () => {
    setBusy(true);
    try {
      const m = await startMeetingNow({ kind: 'room' });
      navigate(`/meetings/${m.id}/record`, { state: { meeting: m } });
    } catch (err) {
      setBusy(false);
      if (onError) onError(adhocNotReady(err) ? 'Ad-hoc meetings need migration 090 on the server.' : reasonOf(err, 'Could not start the meeting.'));
    }
  };
  return (
    <button type="button" className={`btn btn-primary tds-btn mt-start-now ${className}`} disabled={busy} onClick={go}
      title="A meeting for right now: the recorder opens at once, the details can wait">
      <Icon name="mic" size={15} /> {busy ? 'Starting…' : compact ? 'Start now' : 'Start meeting now'}
    </button>
  );
}

export function RecordCallDialog({ onClose, onError }) {
  const navigate = useNavigate();
  const rule = useConsentRule();
  const [platform, setPlatform] = useState('teams');
  const [told, setTold] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const supported = captureSupported();
  const go = async () => {
    if (!told) { setError('Confirm that the people on the call know it is being recorded.'); return; }
    setBusy(true);
    setError(null);
    try {
      const m = await startMeetingNow({ kind: 'call', platform });
      navigate(`/meetings/${m.id}/record?consent=1`, { state: { meeting: m, consent: true } });
    } catch (err) {
      setBusy(false);
      const words = adhocNotReady(err) ? 'Ad-hoc meetings need migration 090 on the server.' : reasonOf(err, 'Could not start the recording.');
      setError(words);
      if (onError) onError(words);
    }
  };
  return (
    <Dialog label="Record a call on another app" onClose={onClose} className="tds-panel mt-call-dialog">
      <div className="mt-call-form">
        <p className="mt-quiet">
          A call that is happening on Teams, Zoom, Slack or the phone. Jarvis records your microphone together with the sound of the
          call and makes the same transcript, summary, minutes and to-dos as for any meeting.
        </p>
        <fieldset className="mt-fieldset mt-platforms">
          <legend>Which app</legend>
          <div className="mt-platform-opts" role="radiogroup" aria-label="Which app the call is on">
            {PLATFORMS.map(([k, w]) => (
              <label key={k} className={`mt-kind-opt mt-platform-opt${platform === k ? ' is-on' : ''}`}>
                <input type="radio" name="mt-platform" value={k} checked={platform === k} onChange={() => setPlatform(k)} />
                <span>{w}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <div className={`mt-consent${told ? ' is-on' : ''}`} data-rule={rule}>
          <p className="mt-consent-rule"><Icon name="shield" size={14} /> {consentWords(rule, 'call')}</p>
          <label className="mt-check mt-consent-check">
            <input type="checkbox" checked={told} onChange={(e) => { setTold(e.target.checked); if (e.target.checked) setError(null); }} />
            <span><strong>{rule === 'ask' ? 'I have asked, and everyone on the call agrees to the recording' : 'I have told everyone on the call that it is being recorded'}</strong>
              <small>Kept on the recording, with the time and your name.</small></span>
          </label>
        </div>
        <p className={`mt-capture-hint${supported ? '' : ' is-warn'}`} role="note">
          <Icon name={supported ? 'sliders' : 'flag'} size={14} /> {supported ? 'Next, the browser asks what to capture. ' : ''}{captureHint()}
        </p>
        {error && <p className="mt-error" role="alert">{error}</p>}
        <div className="tds-dialog-foot">
          <button type="button" className="btn btn-outline tds-btn" disabled={busy} onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary tds-btn mt-call-continue" disabled={busy || !told} onClick={go}>
            {busy ? 'Starting…' : 'Continue to capture'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

export function RecordCallButton({ className = '', onError, compact = false }) {
  const [open, setOpen] = useState(false);
  const may = useMayStart();
  if (!may) return null;
  return (
    <>
      <button type="button" className={`btn btn-outline tds-btn mt-record-call ${className}`} onClick={() => setOpen(true)}
        title="Record a Teams, Zoom, Slack or phone call that is happening in another app">
        <Icon name="share" size={15} /> {compact ? 'Record a call' : 'Record a call on another app'}
      </button>
      {open && <RecordCallDialog onClose={() => setOpen(false)} onError={onError} />}
    </>
  );
}
